import { FC, memo, MouseEvent, ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DropdownArrowIcon } from '@/components/ui/Icons/Icons';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { FilterTypeEnum } from '@/constants/common';
import { useFilterPreparedData } from '@/hooks/useFilterPreparedData';
import { FormValuesKeys } from '@/types';

import { DEFAULT_VALUES } from './Filters.constants';
import * as S from './Filters.styles';

interface FilterProps {
  title: string;
  formField: FormValuesKeys | FormValuesKeys[];
  isArrowResized?: boolean;
  children: ReactNode;
  type: FilterTypeEnum;
  onResetFilter?: (formField: FormValuesKeys | FormValuesKeys[]) => void;
  isOpen: boolean;
  onToggle: (title: string) => void;
}

// Memoized with a stable onToggle so toggling the drawer or one accordion
// doesn't re-render every filter row (the category tree is expensive).
const FilterComponent: FC<FilterProps> = ({
  title,
  formField,
  isArrowResized = false,
  children,
  type,
  onResetFilter,
  isOpen,
  onToggle
}) => {
  const { t } = useTranslation();
  const { resetField, control } = useFormContext();

  const fieldValue = useWatch({ control, name: formField as string });

  const { hasValue, selectedValueText } = useFilterPreparedData({
    formField,
    fieldValue,
    type
  });

  const handleReset = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    onResetFilter?.(formField);

    if (Array.isArray(formField)) {
      formField.forEach(field => {
        resetField(field, { defaultValue: DEFAULT_VALUES[field] });
      });
    } else {
      resetField(formField, { defaultValue: DEFAULT_VALUES[formField] });
    }
  };

  return (
    <S.FilterWrapper $isOpen={isOpen}>
      <S.FilterHeader onClick={() => onToggle(title)}>
        <S.FilterTexts>
          <Typography variant="title3">{t(title)}</Typography>

          <S.SelectedInfo>
            <S.SelectedOption>
              {hasValue ? selectedValueText : t('Filter.no_matter')}
            </S.SelectedOption>
            {hasValue && (
              <S.ResetButton onClick={handleReset} type="button">
                <Typography variant="link_14">
                  {t('Filter.reset_button')}
                </Typography>
              </S.ResetButton>
            )}
          </S.SelectedInfo>
        </S.FilterTexts>

        <S.Arrow $isArrowResized={isArrowResized} $isOpen={isOpen}>
          <DropdownArrowIcon />
        </S.Arrow>
      </S.FilterHeader>

      <S.FilterContent $isOpen={isOpen}>{children}</S.FilterContent>
    </S.FilterWrapper>
  );
};

export const Filter = memo(FilterComponent);
